import type { UpdateConsumerRequest } from '@/services/consumerService'
import { editStartValue, type EditField } from '@/utils/consumerDetailCards'

/**
 * An optional consumer field the Gateway stores in a constrained column.
 * The Gateway writes any value that is not null, and a blank string fails the
 * column check (location_id is an integer_path), so a field the user left empty
 * has to travel as null rather than ''.
 */
export const blankToNull = (value: unknown): string | null => {
  if (value === null || value === undefined) return null
  const text = String(value).trim()
  return text === '' ? null : text
}

type SaveBody = Partial<
  Pick<
    UpdateConsumerRequest,
    'locationId' | 'billingId' | 'coordinates' | 'itmCoordinates' | 'meterNotes'
  >
>

const LATITUDE_AND_LONGITUDE_MESSAGE =
  'קואורדינטות: יש להזין קו רוחב וקו אורך, מופרדים בפסיק'
const GRID_MESSAGE =
  'קואורדינטות לפי רשת ישראל: יש להזין שני מספרים מופרדים בפסיק'
const BILLING_ID_MESSAGE = 'כרטיס מים חייב להיות מספר שלם'

const numberPair = (text: string): [number, number] | null => {
  const parts = text.split(',').map(part => part.trim())
  if (parts.length !== 2 || parts.some(part => part === '')) return null
  const pair = parts.map(Number)
  return pair.every(Number.isFinite) ? [pair[0], pair[1]] : null
}

const latitudeAndLongitude = (text: string): [number, number] | null => {
  const pair = numberPair(text)
  if (!pair || Math.abs(pair[0]) > 90 || Math.abs(pair[1]) > 180) return null
  return pair
}

const FIELDS: EditField[] = [
  'locationId',
  'billingId',
  'coordinates',
  'itmCoordinates',
  'meterNotes',
]

const edited = (
  details: Record<string, any>,
  values: Record<string, unknown>,
  field: EditField
): string | null => {
  if (!(field in values)) return null
  const after = String(values[field] ?? '').trim()
  return after === editStartValue(details, field).trim() ? null : after
}

/** The message for the first edit the Gateway could not take, or null. */
export const saveFieldError = (
  details: Record<string, any>,
  values: Record<string, unknown>
): string | null => {
  const billing = edited(details, values, 'billingId')
  if (billing !== null && !/^\d+$/.test(billing)) return BILLING_ID_MESSAGE
  const point = edited(details, values, 'coordinates')
  if (point !== null && !latitudeAndLongitude(point))
    return LATITUDE_AND_LONGITUDE_MESSAGE
  const grid = edited(details, values, 'itmCoordinates')
  if (grid !== null && !numberPair(grid)) return GRID_MESSAGE
  return null
}

/**
 * The PUT body for the rows the user changed. Only the area, the water card, the two
 * coordinate rows and the meter notes are the utility's own data; every other row of the
 * cards is read-only. A cleared meter note travels as '' because core.assets.notes is
 * NOT NULL, so it skips blankToNull.
 * The point travels by latitude and longitude (the legacy lat and lan fields are exchanged
 * against their names, so both pairs are filled). A grid point travels only when the point
 * itself did not change, because the Gateway derives one from the other.
 */
export const changedSaveFields = (
  details: Record<string, any>,
  values: Record<string, unknown>
): SaveBody => {
  const body: SaveBody = {}
  for (const field of FIELDS) {
    const after = edited(details, values, field)
    if (after === null) continue
    if (field === 'locationId') body.locationId = blankToNull(after)
    else if (field === 'billingId') body.billingId = Number(after)
    else if (field === 'meterNotes') body.meterNotes = after
    else if (field === 'coordinates') {
      const [latitude, longitude] = latitudeAndLongitude(after) ?? []
      if (latitude !== undefined && longitude !== undefined)
        body.coordinates = {
          latitude,
          longitude,
          lat: longitude,
          lan: latitude,
        }
    } else if (!body.coordinates) {
      const [x, y] = numberPair(after) ?? []
      if (x !== undefined && y !== undefined) body.itmCoordinates = { x, y }
    }
  }
  return body
}

const REFUSALS: Record<string, string> = {
  'billingId already exists': 'כרטיס מים כבר שייך לצרכן אחר',
}

/** A Gateway refusal in Hebrew when the dashboard knows it; any other text as the Gateway sent it. */
export const saveRefusalText = (message: string): string =>
  REFUSALS[message.trim()] ?? message
